import { useCallback, useEffect, useRef, useState } from 'react';
import { Adornment, Checkbox, IconChevronDown, Input, Label, LabeledControl } from '@zillow/constellation';
import { StyledAdornedInput, StyledDropdownContainer, StyledDropdownItem } from './StyledComponents';

type OptionsType = Array<{
  isChecked: boolean;
  label: string;
}>;

interface MultiSelectProps {
  label: string;
  onChange: (index: number, isNotChecked: boolean) => void;
  options: OptionsType;
}

export const MultiSelect = ({ label, onChange = () => {}, options = [] }: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const wrapper = useRef(null);

  const indexOptionsMap = useRef<Record<number, number>>({});
  const getNonfilteredIndex = (filteredIndex: number) => {
    return filteredIndex in indexOptionsMap.current ? indexOptionsMap.current[filteredIndex] : filteredIndex;
  };

  const searchFilter = useCallback(
    (allOptions: OptionsType) => {
      if (!inputText) return allOptions;

      indexOptionsMap.current = {};
      let filteredIndex = 0;
      const filteredOptions = allOptions.filter((option, i) => {
        const keepOption = option.label.startsWith(inputText);
        if (keepOption) {
          indexOptionsMap.current[filteredIndex] = i;
          filteredIndex += 1;
        }
        return keepOption;
      });

      return filteredOptions;
    },
    [inputText],
  );

  // @ts-ignore
  const handleDocumentClick = (event) => {
    // @ts-ignore
    if (wrapper.current && !wrapper.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(
    () => () => {
      window.removeEventListener('touchstart', handleDocumentClick);
      window.removeEventListener('mousedown', handleDocumentClick);
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) {
      window.removeEventListener('touchstart', handleDocumentClick);
      window.removeEventListener('mousedown', handleDocumentClick);
    } else {
      window.addEventListener('touchstart', handleDocumentClick);
      window.addEventListener('mousedown', handleDocumentClick);
    }
  }, [isOpen]);

  const renderPanel = () => {
    return (
      <StyledDropdownContainer>
        {searchFilter(options).map((o, i) => (
          <StyledDropdownItem paddingX="sm" key={i}>
            <LabeledControl
              label={<Label>{o.label}</Label>}
              control={
                <Checkbox checked={o.isChecked} onChange={() => onChange(getNonfilteredIndex(i), !o.isChecked)} />
              }
            />
          </StyledDropdownItem>
        ))}
      </StyledDropdownContainer>
    );
  };

  const renderPreviewValue = () => {
    const checkedOptions = options.filter((o) => o.isChecked).map((o) => o.label);
    return checkedOptions.length ? `(${checkedOptions.length}) Selected` : '';
  };

  const handleToggle = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
    setInputText('');
  };

  return (
    <div className="MultiSelect" ref={wrapper}>
      <Label>{label}</Label>
      <StyledAdornedInput
        input={
          <Input
            value={inputText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputText(e.target.value)}
            autoComplete="true"
          />
        }
        value={renderPreviewValue()}
        readOnly={!isOpen}
        onClick={handleToggle}
        rightAdornment={
          <Adornment>
            <IconChevronDown />
          </Adornment>
        }
      />
      {isOpen && renderPanel()}
    </div>
  );
};

export default MultiSelect;
